import type { Transport, TransportEvent } from '../types';
import { extractText, postChat, readLines } from './http';

/**
 * Server-Sent Events (`text/event-stream`).
 *
 * Accepted frames:
 *   data: {"delta":"Hel"}          -> appended
 *   data: {"text":"Hello"}         -> appended
 *   data: plain text               -> appended
 *   data: [DONE]                   -> ends the stream
 *   event: meta\ndata: {...}       -> merged into message.meta
 *   event: error\ndata: {...}      -> reported as an error
 *   event: message\ndata: {...}    -> replaces content (non-incremental servers)
 */
export const sseTransport: Transport = async function* (req) {
  const response = await postChat(req, true);

  let eventName = '';
  let dataLines: string[] = [];

  const flush = (): TransportEvent | 'done' | null => {
    if (dataLines.length === 0) {
      eventName = '';
      return null;
    }
    const raw = dataLines.join('\n');
    const name = eventName;
    dataLines = [];
    eventName = '';

    if (raw === '[DONE]' || name === 'done' || name === 'end') return 'done';

    let parsed: unknown = raw;
    if (raw.startsWith('{') || raw.startsWith('[')) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = raw;
      }
    }

    if (name === 'error') {
      const text = extractText(parsed);
      return { type: 'error', error: text || (typeof raw === 'string' ? raw : 'Stream error') };
    }
    if (name === 'meta' || name === 'metadata') {
      return typeof parsed === 'object' && parsed !== null
        ? { type: 'meta', data: parsed as Record<string, unknown> }
        : null;
    }

    const text = extractText(parsed);
    if (!text) {
      // A JSON frame with no text but useful fields still carries metadata.
      if (typeof parsed === 'object' && parsed !== null) {
        return { type: 'meta', data: parsed as Record<string, unknown> };
      }
      return null;
    }
    return { type: name === 'message' || name === 'replace' ? 'message' : 'delta', text };
  };

  for await (const line of readLines(response)) {
    if (line === '') {
      const event = flush();
      if (event === 'done') return;
      if (event) yield event;
      continue;
    }
    if (line.startsWith(':')) continue; // comment / keep-alive
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      const value = line.slice(5);
      dataLines.push(value.startsWith(' ') ? value.slice(1) : value);
    }
    // `id:` and `retry:` are accepted and ignored.
  }

  const trailing = flush();
  if (trailing && trailing !== 'done') yield trailing;
};
